import {
  AUTO_AFILTER_CACHE_KEY,
  AUTO_FILTER_CACHE_DURATION_IN_SECONDS,
  BIG_FILTER_CACHE_DURATION_IN_SECONDS,
  IS_AUTO_AFILTER_CACHE_ENABLE,
  IS_FILTERS_CACHE_ENABLE,
  MOCK_FILTERS_URL,
  TREE_LEFT_PADDING,
} from "@/constants";

import { getCljsField, isStoryBookDomain } from "@/utils";
import { CacheService, HttpService, TreeService } from "@/services";
import {
  IFiltersServerResponse,
  INestedCriteria,
  IPanelScope,
  ITreeItem,
} from "@/interfaces";
import { FilterTypes, LocalStorageKeys } from "@/enums";
import { createElement } from "react";
import { MuxAutoFilter } from "@/Icons";
import {
  findNodeById,
  mergeUrlParams,
  parseUrl,
  shouldShowExpandOption,
} from "@/utils/common";

const fetchFilterUrl = (
  projectId: string,
  entityId: string,
  module: string,
  autoFilterNode?: string,
): string => {
  return `/projects/${projectId}/custom_views/${entityId}/fetch_filter?entity_name=${module}&auto_filter=${autoFilterNode}`;
};

const filtersService = () => {
  const fetchFiltersFromServer = async (
    scope: IPanelScope,
  ): Promise<{ filters: ITreeItem[] }> => {
    const { module, projectId } = scope;

    const cachedObj = IS_FILTERS_CACHE_ENABLE
      ? CacheService.getItemFromPersistCache(
          scope,
          LocalStorageKeys.FILTERS_ITEMS_KEY,
          false,
        )
      : undefined;

    const viewsCount = cachedObj?.viewsCount || -1;
    const cachedFilters = cachedObj?.items;

    const rawListOfNodeShouldBeFetch = TreeService.getOpenedNodes(scope);
    // Note: Feature flag filtering will be applied later when server response is available
    const listOfNodeShouldBeFetch = rawListOfNodeShouldBeFetch;

    const activeNode = TreeService.getActiveNode(scope);
    const activeFilterId = activeNode?.id;
    const urlFilterId = FiltersService.getActiveFilterIdFromUrl();
    // URL filter takes precedence over stored active node (e.g. dashboard drill-down)
    const fullSelectedItem = urlFilterId || activeFilterId;
    const selectedItem = fullSelectedItem?.split("/")[0];

    const activeNodeAncestries: string[] = [];
    if (activeNode?.serverObject?.ancestry) {
      activeNodeAncestries.push(...activeNode.serverObject.ancestry.split("/"));
    }
    if (fullSelectedItem?.includes("/")) {
      const parts = fullSelectedItem.split("/");
      let currentPath = parts[0];
      activeNodeAncestries.push(currentPath);
      for (let i = 1; i < parts.length - 1; i += 2) {
        if (i + 1 < parts.length) {
          currentPath += `/${parts[i]}/${parts[i + 1]}`;
          activeNodeAncestries.push(currentPath);
        }
      }
      if (!activeNodeAncestries.includes(fullSelectedItem)) {
        activeNodeAncestries.push(fullSelectedItem);
      }
    }

    listOfNodeShouldBeFetch.push(...activeNodeAncestries);
    selectedItem && listOfNodeShouldBeFetch.push(selectedItem);
    const openIds = [...new Set(listOfNodeShouldBeFetch)].join(",");

    const fetchFiltersApi = isStoryBookDomain
      ? MOCK_FILTERS_URL
      : `/projects/${projectId}/custom_views/left_pane_views` +
        `?entity_name=${module}` +
        `&ids=${openIds}` +
        `&views_count=${viewsCount}`;

    const data = await HttpService.get(fetchFiltersApi);

    if (data.items && (selectedItem || fullSelectedItem)) {
      let selectedNode = fullSelectedItem
        ? findNodeById(data.items, fullSelectedItem)
        : null;
      if (!selectedNode && selectedItem) {
        selectedNode = findNodeById(data.items, selectedItem);
      }

      if (selectedNode) {
        const openNodes = TreeService.getOpenedNodes(scope);
        const ancestry = selectedNode?.ancestry?.split("/") || [];
        openNodes.push(...ancestry);
        const filteredNodes = [...new Set(openNodes)];
        TreeService.setOpenedNodes(scope, filteredNodes);
      }
    }

    CacheService.removeItemFromPersistCache(
      scope,
      LocalStorageKeys.FILTERS_ITEMS_KEY,
    );

    if (!data?.items && !cachedFilters) {
      console.error(
        `filtersService->fetchFiltersFromServer Both filters and cache filters cannot be undefined simultaneously.`,
      );
      throw `Both filters and cache filters cannot be undefined simultaneously.`;
    }

    const serverFilters = data?.items ? data.items : cachedFilters;

    try {
      CacheService.saveItemToInMemoryCache(
        CacheService.getLocalStorageKey(
          scope,
          LocalStorageKeys.FILTERS_ITEMS_KEY,
        ),
        {
          items: data.items,
          viewsCount: data.views_count,
        },
        BIG_FILTER_CACHE_DURATION_IN_SECONDS,
      );
    } catch (e) {
      console.error(
        `fetchFiltersFromServer-> failed to save filter to localstorage `,
        e,
      );
    }

    const openedNodes = TreeService.getOpenedNodes(scope);

    // Add the active Node, the active node not always in the opened nodes
    const activeNodeVal = TreeService.getActiveNode(scope).id;

    let allOpenNodes = [...openedNodes, activeNodeVal];
    if (fullSelectedItem && fullSelectedItem.includes("/")) {
      const missingAncestries = activeNodeAncestries.filter(
        (path) => !allOpenNodes.includes(path),
      );
      allOpenNodes = [...allOpenNodes, ...missingAncestries];
    }

    const result = await transformServerToClient(
      serverFilters,
      undefined,
      allOpenNodes,
      scope,
      new Set(),
    );

    return { filters: result };
  };

  const getCacheAutoFilters = (key: string): ITreeItem | undefined => {
    const item: unknown | undefined =
      CacheService.getItemFromInMemoryCache(key);
    return item ? (item as ITreeItem) : undefined;
  };

  const saveCacheAutoFilters = (key: string, value: ITreeItem): void => {
    CacheService.saveItemToInMemoryCache(
      key,
      value,
      AUTO_FILTER_CACHE_DURATION_IN_SECONDS,
    );
  };

  const fetchFromServer = async (
    fetchUrl: string,
  ): Promise<{ items?: ITreeItem[]; node?: ITreeItem }> => {
    const res = await HttpService.get(fetchUrl);
    const isSingleItem = !!res.node;

    const items: ITreeItem[] = await transformServerToClient(
      isSingleItem ? [res.node] : res.items,
      undefined,
      [],
      undefined,
      new Set(),
    );

    return isSingleItem ? { node: items[0] } : { items };
  };

  const fetchAutoFilterChildrenUrl = (
    projectId: string,
    node: ITreeItem,
    module: string,
  ) => {
    const params = new URLSearchParams();
    params.append("node", JSON.stringify(node.serverObject));
    params.append("entity_name", module);
    return `/projects/${projectId}/custom_views/fetch_auto_filter_children?${params.toString()}`;
  };

  const fetchAutoFilterChildren = async (
    scope: IPanelScope,
    node: ITreeItem,
  ) => {
    // Fetch auto filter endpoint, bring children of an auto filter child node.
    const { projectId, module } = scope;
    const url: string = fetchAutoFilterChildrenUrl(projectId, node, module);
    const cacheKey: string =
      AUTO_AFILTER_CACHE_KEY + fetchFilterUrl(projectId, node.id, module);
    return await fetchAndCacheFilter(url, cacheKey).then((res) => res);
  };

  const fetchAutoFilterChildrenFromServer = async (
    scope: IPanelScope,
    node: ITreeItem,
  ) => {
    // Fetch auto filter endpoint, bring children of an auto filter child node.
    const { projectId, module } = scope;
    const url: string = fetchAutoFilterChildrenUrl(projectId, node, module);

    try {
      const res = await HttpService.get(url);
      return res.node;
    } catch (error) {
      console.error(
        `fetchAutoFilterChildrenFromServer error for ${node.id}:`,
        error,
      );
      return null;
    }
  };

  const fetchAndCacheFilter = async (url: string, cacheKey: string) => {
    const res = await HttpService.get(url);

    const filters: ITreeItem[] = await transformServerToClient(
      [res.node],
      undefined,
      [],
      undefined,
      new Set(),
    );
    const filter = filters?.[0];
    if (filter && IS_AUTO_AFILTER_CACHE_ENABLE) {
      saveCacheAutoFilters(cacheKey, filter);
    }
    return filter;
  };

  const fetchFilterFromServer = async (
    scope: IPanelScope,
    filterId: string,
    autoFilterNode: string = "",
  ): Promise<ITreeItem> => {
    const { projectId, module } = scope;

    const fetchFilterApi = isStoryBookDomain
      ? MOCK_FILTERS_URL
      : fetchFilterUrl(projectId, filterId, module, autoFilterNode);

    const cacheAutoFilter = IS_AUTO_AFILTER_CACHE_ENABLE
      ? getCacheAutoFilters(AUTO_AFILTER_CACHE_KEY + fetchFilterApi)
      : undefined;
    if (cacheAutoFilter) {
      return cacheAutoFilter;
    }
    return await fetchAndCacheFilter(
      fetchFilterApi,
      AUTO_AFILTER_CACHE_KEY + fetchFilterApi,
    ).then((res) => res);
  };

  const fetchFiltersFromCache = ({
    module,
    projectId,
  }: IPanelScope): IFiltersServerResponse | null => {
    const key = `filters-${module}-${projectId}`.toLowerCase();
    const cachedFilters = localStorage.getItem(key);
    return cachedFilters ? JSON.parse(cachedFilters) : null;
  };

  const getFilterType = (node: any): FilterTypes => {
    let filterType: FilterTypes = FilterTypes.USER;

    if (node?.["auto"]?.length) {
      filterType = FilterTypes.AUTO_FILTER_ROOT;
    } else if (node?.["auto_filter"]?.length) {
      filterType = FilterTypes.AUTO_FILTER;
    }

    return filterType;
  };

  const prepareServerObject = (node: any): any => {
    return {
      ...node,
      children: [], // explicitly set children to an empty array
    };
  };

  const hasAutoFilterIcon = (item: ITreeItem): boolean => {
    const filterType = getFilterType(item);
    return (
      filterType === FilterTypes.AUTO_FILTER_ROOT ||
      !!item?.auto?.length ||
      (filterType === FilterTypes.AUTO_FILTER &&
        (!!item?.children?.length || !!item?.has_children))
    );
  };

  const getFilterIcon = (item: ITreeItem): React.JSX.Element | null => {
    if (!hasAutoFilterIcon(item)) return null;
    return createElement(
      "span",
      {
        style: {
          color: "inherit",
        },
      },
      createElement(MuxAutoFilter),
    );
  };

  const getPaddingLeft = (item: any): number => {
    if (
      !item.children?.length &&
      !item.has_children &&
      !hasAutoFilterIcon(item)
    ) {
      return TREE_LEFT_PADDING;
    }
    return 0;
  };

  const transformServerToClient = async (
    node: any,
    parentType: FilterTypes | undefined = undefined,
    openedNodes: string[] = [],
    scope?: IPanelScope | undefined,
    fetchedItemIds: Set<string> = new Set(),
  ): Promise<ITreeItem[]> => {
    const filters: ITreeItem[] = [];
    for (const item of node) {
      if (!item?.id) {
        continue;
      }
      const filterType = getFilterType(item);

      const treeItem: ITreeItem = {
        serverObject: prepareServerObject(item),
        key: `${item.id}-${item.name}`,
        name: item.name,
        id: item.id + "",
        ancestry: item.ancestry,
        children: [],
        extra: {
          getIcon: () => getFilterIcon(item),
          paddingLeft: getPaddingLeft(item),
          parentType,
          filterType,
          autoFilterMetaData: getRootAutoFilterMetaData(item),
          isLoading: false,
          hasChildren: shouldShowExpandOption(item),
        },
      };
      // Check if this is an opened node that needs its children fetched
      if (
        scope &&
        openedNodes.includes(`${item.id}`) &&
        item.has_children &&
        item.is_auto_filter_child &&
        !item.children.length &&
        !fetchedItemIds.has(`${item.id}`)
      ) {
        // Add item ID to tracking set to prevent infinite loops
        fetchedItemIds.add(`${item.id}`);
        try {
          const childFilter = await fetchAutoFilterChildrenFromServer(
            scope,
            treeItem,
          );

          const children = childFilter?.children ?? [];
          item.children = Array.isArray(children) ? children : [];
          // Update hasChildren based on actual fetched children and feature flag
          item.has_children = item.children.length > 0;
          treeItem.extra.hasChildren = shouldShowExpandOption(item);
          treeItem.extra.paddingLeft = childFilter
            ? getPaddingLeft(childFilter)
            : 0;

          treeItem.extra.isLoading = false;
        } catch (error) {
          console.error(
            `Failed to fetch children for filter ${item.id}:`,
            error,
          );
          // Ensure we have an empty children array even on error
          item.children = [];
          item.has_children = false;
          treeItem.extra.hasChildren = false;
          treeItem.extra.isLoading = false;
        }
      }

      // Process children recursively
      treeItem.children = item?.children?.length
        ? await transformServerToClient(
            item.children,
            filterType,
            openedNodes,
            scope,
            fetchedItemIds,
          )
        : [];
      filters.push(treeItem);
    }
    return filters;
  };

  const getRootAutoFilterMetaData = (item: any): any => {
    if (!item?.["auto"]?.length) {
      return null;
    }

    // Limit to first two levels if feature flag is off
    const autoArrayToProcess = item.auto;

    // Dynamically process all levels in the auto array
    const metadata: Record<string, any> = {};

    autoArrayToProcess.forEach((autoLevel: any, index: number) => {
      if (!autoLevel) return;

      // Clean, consistent TypeScript approach using template literals
      const levelKey = `level${index}List` as const;
      const flattenedValues = flattenValues(autoLevel);

      metadata[levelKey] = flattenedValues;

      // Maintain backward compatibility for existing code
      if (index === 0) metadata.list = flattenedValues;
      if (index === 1) metadata.subList = flattenedValues;
    });

    // Also store the raw auto array for reference
    metadata.rawAuto = autoArrayToProcess;
    metadata.levelCount = autoArrayToProcess.length;

    return metadata;
  };

  const deleteFilter = async (scope: IPanelScope, filterId: string) => {
    await HttpService.delete(
      `/projects/${scope.projectId}/custom_views/${filterId}`,
    );
  };

  const getDeletionPreventionStatus = async (
    scope: IPanelScope,
    filterId: string,
  ) => {
    return await HttpService.get(
      `/projects/${scope.projectId}/custom_views/${filterId}/deletion_prevention_status`,
    );
  };

  function flattenValues(input: any): string[] | null {
    if (!input) {
      return null;
    }

    const result: any[] = [];

    function traverse(node: any) {
      if (Array.isArray(node)) {
        node.forEach((element) => traverse(element));
      } else if (typeof node === "object" && node !== null) {
        Object.values(node).forEach((value) => traverse(value));
      } else {
        node && result.push(node.toLowerCase ? node.toLowerCase() : node);
      }
    }

    traverse(input);
    const items = result.filter((r) => !!r);
    return items?.length > 0 ? items : null;
  }
  const pushToHistory = (
    node: ITreeItem | { id: string },
    isInitialLoad: boolean = false,
  ): string => {
    let searchParams = `cv=${node.id}`;
    if (isInitialLoad) {
      const filters = getGridFilters();
      if (Object.entries(filters).length > 0) {
        const params = window.location.search.split("&");
        const filterParam = params.find((p) => p.startsWith("filter%5B"));
        searchParams += "&" + filterParam;
      }
    }

    let newUrl = window.location.pathname;
    const hash = window.location.hash.split("?")[0];
    if (searchParams) {
      newUrl = `${newUrl}${hash}?${searchParams.toString()}`;
    }

    const currentUrl = window.location.href;
    const mergedUrl = mergeUrlParams(currentUrl, `${window.origin}${newUrl}`);

    // Save the current state before replacing it
    const currentState = history.state || {};
    currentState.previousParams = parseUrl(currentUrl).params.toString();

    // Update the URL without reloading the page
    isInitialLoad
      ? window.history.pushState(currentState, "", mergedUrl)
      : window.history.replaceState(currentState, "", mergedUrl);

    return newUrl;
  };

  interface GridFilterRangeValue {
    [subfield: string]: string;
  }

  interface GridFilter {
    [field: string]: string[] | GridFilterRangeValue;
  }

  const getGridFilters = (): GridFilter => {
    const queryParams = new URLSearchParams(window.location.search);
    const gridFilter: GridFilter = {};

    /**
     * Adds a field-value pair to the filter if both are valid
     */

    const isReferredFromInstanceDashboard = () => {
      const queryParams = new URLSearchParams(window.location.search);
      return queryParams.get("instance_view_mode") === "true";
    };

    const buildFieldKey = (rawPath: string): string => {
      // Define regex patterns and their replacements
      let cleanedPath = rawPath;

      if (isReferredFromInstanceDashboard()) {
        const replacements = [
          { pattern: /suites\./g, replacement: "_suite_-" },
          { pattern: /qtest_designs\./g, replacement: "_test_-" },
          { pattern: /qtest_runs\./g, replacement: "_lastrun_-" },
          { pattern: /fields -> /g, replacement: "" },
          { pattern: /'/g, replacement: "" },
        ];

        // Apply all replacements

        for (const { pattern, replacement } of replacements) {
          cleanedPath = cleanedPath.replace(pattern, replacement);
        }
      }

      // Extract the last segment if path contains dots
      return cleanedPath.includes(".")
        ? cleanedPath.split(".").pop()!
        : cleanedPath;
    };

    // Process filter parameters in the form of:
    // - filter[field][] for array values
    // - filter[field][subfield] for nested object values
    queryParams.forEach((value, key) => {
      const filterMatch = key.match(/^filter\[(.+?)]\[(.*)]/);
      if (!filterMatch || !filterMatch[1]) return;

      const field = buildFieldKey(filterMatch[1]);
      const subfield = filterMatch[2];

      if (subfield) {
        const current = (gridFilter[field] ||= {}) as GridFilterRangeValue;
        current[subfield] = value;
        return;
      }

      const current = (gridFilter[field] ||= []) as string[];
      current.push(value);
    });

    return gridFilter;
  };

  const getActiveFilterIdFromUrl = (): string | null => {
    const searchParams = new URLSearchParams(
      window.location.search || window.location.hash.split("?")?.[1],
    );
    //old ui support
    if (searchParams.get("cv")) {
      return searchParams.get("cv");
    }

    if (window.location.search.includes("auto_filter[")) {
      // Support Old Ui BookMarks
      const autoFilterParams: Record<string, string> = {};
      for (const [key, value] of searchParams.entries()) {
        if (key.startsWith("auto_filter")) {
          const paramName = key.replace("auto_filter[", "").replace("]", "");
          autoFilterParams[paramName] = value;
        }
      }

      const id = `${autoFilterParams.parent}/${autoFilterParams.field}/${autoFilterParams.value}${autoFilterParams.sub_value ? `/version/${autoFilterParams.sub_value}` : ""}`;
      return id;
    }
    return null;
  };

  const rename = async (
    projectId: string,
    filterId: string,
    value: string,
    items: ITreeItem[],
  ) => {
    await HttpService.put(
      `/projects/${projectId}/custom_views/${filterId}/update_filter_name`,
      {
        filter_name: value,
      },
    );

    const treeNode = findNodeById(items, filterId);
    if (treeNode) {
      treeNode.name = value;
      treeNode.serverObject.name = value;
    }
  };

  const getFieldLabel = (val: string): string => {
    return val !== "other_filter" ? val : "Other/Empty values";
  };

  function findSubById(
    obj: INestedCriteria,
    id: string,
  ): INestedCriteria | null {
    if (obj.data.filter_id == id) {
      return obj;
    }
    if (obj.sub) {
      return findSubById(obj.sub, id);
    }
    return null;
  }

  const appendAutoFilterToCriteriaTree = (
    res: INestedCriteria,
    node: ITreeItem,
  ): INestedCriteria => {
    const { ancestry, field, sub_field, auto_filter, is_auto_filter_child } =
      node.serverObject;

    if (!is_auto_filter_child) {
      return res;
    }
    const parentId = ancestry.split("/").pop();
    const currFilterSub = findSubById(res, parentId);
    if (currFilterSub) {
      // Build criteria tree dynamically for all auto_filter levels
      let currentSub = currFilterSub;

      // Process all levels in auto_filter array
      if (auto_filter && Array.isArray(auto_filter)) {
        // Use the feature flag passed as parameter

        const autoFilterToProcess = auto_filter;

        for (let i = 0; i < autoFilterToProcess.length; i++) {
          const filterValue = autoFilterToProcess[i];
          if (filterValue) {
            // Use different fields for each level if available, otherwise fall back to main field
            const levelField = node.serverObject.fields?.[i] || field;
            const fieldData = getCljsField(levelField);
            const fieldDataValue = getFieldLabel(filterValue);

            currentSub.sub = {
              data: {
                filter_id:
                  i === autoFilterToProcess.length - 1
                    ? node.serverObject.id
                    : field,
                filter_name: `${fieldData?.label} Is ${fieldDataValue}`,
                is_auto_filter: true,
                criterias: [],
              },
              sub: null,
            };

            // Move to the next level for the next iteration
            currentSub = currentSub.sub;
          }
        }
      }

      // Legacy support for old field/sub_field approach
      if (field && auto_filter?.[1]) {
        const fieldData = getCljsField(field);
        const fieldDataValue = getFieldLabel(auto_filter[0]);

        currentSub.sub = {
          data: {
            filter_id: field,
            filter_name: `${fieldData?.label} Is ${fieldDataValue}`,
            is_auto_filter: true,
            criterias: [],
          },
          sub: null,
        };

        const subAutoFilterValue = auto_filter?.[1];
        if (sub_field && subAutoFilterValue) {
          const value = getFieldLabel(subAutoFilterValue);
          const subFieldData = sub_field ? getCljsField(sub_field) : undefined;

          currentSub.sub.sub = {
            data: {
              filter_id: node.serverObject.id,
              filter_name: `${subFieldData?.label} Is ${value}`,
              is_auto_filter: true,
              criterias: [],
            },
            sub: null,
          };
        }
      }
    }
    return res;
  };

  return {
    getFilterType,
    fetchFiltersFromCache,
    fetchFromServer,
    fetchFiltersFromServer,
    fetchFilterFromServer,
    fetchAutoFilterChildren,
    deleteFilter,
    getDeletionPreventionStatus,
    getGridFilters,
    pushToHistory,
    getActiveFilterIdFromUrl,
    rename,
    appendAutoFilterToCriteriaTree,
    transformServerToClient,
    fetchAutoFilterChildrenFromServer,
  };
};

export const FiltersService = filtersService();
